import DOMPurify from 'dompurify';
import { ProductGtmData } from '@jbhi-fi/apis/src/algolia/types/ProductGtmData';
import { transformProductGtmDataForTracking } from '../../../../utils/cart/transformProductGtmDataForTracking';
import { toCamelCase } from '@jbhi-fi/utils/src/toCamelCase';
import { trackProductClick } from '@jbhi-fi/utils/src/ga4Tracking/functions/trackProductClick';
import { getStoreCurrency } from '@jbhi-fi/utils/src/shopify/getStoreCurrency';
import { ProductMetafields } from '../../../../api/shopify/types';
import { AlgoliaProductHit } from '../../../../utils/types';
import { serviceHelper } from '../../../../config/service-helper';
import { EcommerceCartItem } from '@jbhi-fi/utils/src/ga4Tracking/types';
import { IconNameEnum } from '@jbhi-fi/lanyard-ui/components/Icon';
import { showMaxQtyAlert } from '../../../../utils/checkMaxQty/showMaxQtyAlert';
import { useCartContext } from '../../../../utils/cart/context/CartContext';
import { ShopifyStorefrontClient } from '../../../../api/shopify/ShopifyStorefrontClient';
import { updateCart, setCartLoading } from '../../../../utils/cart/cartUtils';
import { filterAndMapAttributes } from '@jbhi-fi/apis/src/utils/filterAndMapAttributes';
import { AccordionIds } from '../accordionContent/AccordionContent';
import { Tracker } from '@jbhi-fi/components/src/Tracking';
import { Coupon } from '@jbhi-fi/components/src/types';
import { sumOfItemPrice } from '@jbhi-fi/utils/src/ga4Tracking/ga4utils';
import { trackAddToCart } from '@jbhi-fi/utils/src/ga4Tracking';
import { transformAlgoliaHitForTracking } from '@jbhi-fi/components/src/Algolia/transformers/transformAlgoliaHitForTracking';
import { AlgoliaApiClient } from '../../../../api/localisation/AlgoliaApiClient';
import { AlgoliaStoreData } from '@jbhi-fi/apis/src/algolia/types/AlgoliaStoreData';

export const ENERGY_STAR_RATING = 'Energy star rating';
export const WATER_STAR_RATING = 'Water star rating [WELS]';

export const isTgaRegulated = (specDetails?: Array<{ Name: string; Values: Array<string> }>): boolean => {
  const TGA_SPEC_NAME = 'TGA-regulated therapeutic good';
  const TGA_SPEC_VALUE = 'Yes';

  return !!specDetails?.some(spec => spec.Name === TGA_SPEC_NAME && spec.Values?.includes(TGA_SPEC_VALUE));
};

const storefrontClient = new ShopifyStorefrontClient();

export const formatStockLevelStatus = (stockLevel: string): string => {
  switch (stockLevel) {
    case 'LastChance':
      return 'Nearly gone';
    case 'SellingFast':
      return 'Selling fast';
    case 'Gone':
      return 'Sold out';
    case 'RunningOut':
      return 'Running out';
    default:
      return '';
  }
};

export const formatExtraDescriptionTitles = (value: string): string => {
  if (value === 'Genre') {
    return 'Genre';
  }
};

export const messageConstants = {
  isFullMessage:
    'Your wishlist has reached capacity. To continue adding to your list, please remove some products from your wishlist.',
  loginMessage: "You're one step away from having this in your wishlist!",
  removeItemMessage: 'Remove from wishlist',
  addedItemMessage: 'Added to wishlist',
  removedItemMessage: 'Removed from wishlist',
};

export const removeHtmlSpace = (value: string): string => {
  if (value) {
    return value.replace(/\&nbsp;/g, '');
  }
};

export const isSoftware = (value: number): boolean => {
  if (value === 110 || value === 100 || value === 92) {
    return true;
  }
};

export const formatLongText = (value: string): string => {
  if (value.length > 25) {
    return `${value.substring(0, 25)}...`;
  }

  return value;
};

export const formatDescriptions = (value: string): string => {
  // Sanitize description body HTML
  let sanitizedHtml = DOMPurify.sanitize(value, {
    ALLOWED_TAGS: ['p', 'b', 'strong', 'a', 'br'],
  });

  // remove non-breaking spaces and empty p tags to maintain formatting
  sanitizedHtml = sanitizedHtml.replace(/<p>&nbsp;<\/p>/, '');
  sanitizedHtml = sanitizedHtml.replace(/<p[^>]*><\/p[^>]*>/, '');
  sanitizedHtml = sanitizedHtml.replace(/<p[^>]*>.<\/p[^>]*>/, '');
  sanitizedHtml = sanitizedHtml.trim();
  return sanitizedHtml;
};

export const removePTags = (value: string): string => {
  return DOMPurify.sanitize(value, { FORBID_TAGS: ['p', 'b'] });
};

export const removeHTMLTags = (value: string) => {
  return DOMPurify.sanitize(value, { USE_PROFILES: { html: false } });
};

export const isEmailValid = (email: string | null): boolean => {
  if (!email) return false;

  return !!email.match(
    /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  );
};

export const getElementPadding = (value: Element) => {
  const rightMargin = getComputedStyle(value)['padding-right'].replace('px', '');
  const leftMargin = getComputedStyle(value)['padding-left'].replace('px', '');
  return parseInt(leftMargin) + parseInt(rightMargin);
};

export const dataLayerPushEvent = async (event: string, gtmData: any, source?: string) => {
  try {
    await window.dataLayer.push({
      event: event,
      source: source,
      pageType: window.gtm.pagetype,
      ecommerce: {
        products: [gtmData],
      },
    });
  } catch (error) {
    console.error('Error submitting dataLayer', error);
  }
};

export const dataLayerPushServiceEvent = async (
  event: string,
  productGtmData: any,
  serviceData: any,
  source?: string,
  message?: string | undefined
) => {
  try {
    await window.dataLayer.push({
      event: event,
      source: source,
      pageType: window.gtm.pagetype,
      message: message,
      ecommerce: {
        products: [productGtmData],
        service: [serviceData],
      },
    });
  } catch (error) {
    console.error('Error submitting dataLayer', error);
  }
};

export const dataLayerPushEventAction = async (event: string, action: string, gtmData: any) => {
  try {
    await window.dataLayer.push({
      event: event,
      action: action,
      pageType: window.gtm.pagetype,
      ecommerce: {
        products: [gtmData],
      },
    });
  } catch (error) {
    console.error('Error submitting dataLayer', error);
  }
};

export const dataLayerVariantEcomEvent = (
  sku: string | undefined,
  googleTagManager: ProductGtmData,
  ecommerceCartItemProps?: Partial<EcommerceCartItem>,
  coupons?: Array<Coupon>
) => {
  try {
    const gtmProduct = toCamelCase(googleTagManager) as ProductGtmData;
    const viewedItem = {
      ...transformProductGtmDataForTracking(gtmProduct, 'productdetail', gtmProduct.id),
      ...ecommerceCartItemProps,
    };
    window.dataLayer.push({
      event: 'productView',
      ecommerce: {
        currencyCode: getStoreCurrency(),
        detail: {
          actionField: { id: sku },
          products: googleTagManager,
        },
      },
    });
    Tracker.trackCustomEvent({
      event: 'view_item',
      location: 'pdp',
      value: sumOfItemPrice(viewedItem),
      ecommerce: {
        currency: getStoreCurrency(),
        items: Array.isArray(viewedItem) ? viewedItem : [viewedItem],
      },
      ...(coupons?.length
        ? {
            coupon_name: coupons?.map(coupon => coupon.content?.displayName).join(' | ') || '',
            coupon: coupons?.map(coupon => coupon.couponName).join(' | ') || '',
            coupon_id: coupons?.map(coupon => coupon.couponExternalId).join(' | ') || '',
            coupon_count: coupons?.length || 0,
          }
        : undefined),
    });
  } catch (error) {
    console.error('Error submitting dataLayer', error);
  }
};

export const dataLayerVariantClickEvent = async (
  sku: string | undefined,
  newSku: string,
  productName: string,
  category: string,
  type: string,
  variant?: string,
  googleTagManager?: ProductGtmData,
  variantFamilyName?: string,
  productGroupName?: string
) => {
  try {
    const gtmProduct = toCamelCase(googleTagManager) as ProductGtmData;
    await window.dataLayer.push({
      event: 'custom.variant.click',
      sku,
      newSku,
      productName,
      category,
      type,
      variant,
    });
    trackProductClick({
      location: 'pdp',
      currency: getStoreCurrency(),
      clickedProduct: transformProductGtmDataForTracking(
        gtmProduct,
        'variants',
        gtmProduct.id,
        variantFamilyName,
        productGroupName
      ),
    });
  } catch (error) {
    console.error('Error submitting dataLayer', error);
  }
};

export const dataLayerPushEventActionWithCategory = async (
  event: string,
  eventCategory: string,
  action: string,
  value: string
) => {
  try {
    window.dataLayer.push({
      event: event,
      eventCategory: eventCategory,
      eventAction: action,
      value: value,
    });
  } catch (error) {
    console.error('Error submitting dataLayer', error);
  }
};

export const getEnergyWaterRatingsSvgs = (
  metafields: ProductMetafields
): { energyRatingSrc?: string; waterRatingSrc?: string; comboEnergyRatingSrcs?: Array<string> } => {
  const energyRating = metafields?.online_product?.value?.Display?.SpecificationDetails?.find(
    detail => detail.Name === ENERGY_STAR_RATING
  )?.Values[0];
  const waterRating = metafields?.online_product?.value?.Display?.SpecificationDetails?.find(
    detail => detail.Name === WATER_STAR_RATING
  )?.Values[0];
  const dyerComboEnergyRating = metafields?.online_product?.value?.Display?.SpecificationDetails?.find(
    detail => detail.Name === 'Washer-dryer energy star rating (Dryer)'
  )?.Values[0];
  const washerComboEnergyRating = metafields?.online_product?.value?.Display?.SpecificationDetails?.find(
    detail => detail.Name === 'Washer-dryer energy star rating (Washer)'
  )?.Values[0];
  const hasComboEnergyRating = dyerComboEnergyRating && washerComboEnergyRating;
  return {
    energyRatingSrc: energyRating
      ? `https://jbhcdn.azureedge.net/public/products/energy-ratings/${energyRating}.png`
      : undefined,
    waterRatingSrc: waterRating
      ? `https://jbhcdn.azureedge.net/public/products/water-ratings/${waterRating}.png`
      : undefined,
    comboEnergyRatingSrcs: hasComboEnergyRating
      ? [
          `https://jbhcdn.azureedge.net/public/products/energy-ratings/dryer-energy-${dyerComboEnergyRating}.png`,
          `https://jbhcdn.azureedge.net/public/products/energy-ratings/washer-energy-${washerComboEnergyRating}.png`,
        ]
      : undefined,
  };
};

export const getDimensionsSvg = (productGroupId: number | undefined): string => {
  return `${serviceHelper.jbCdnBaseUrl}/products/dimensions-with-specs/${productGroupId}.svg`;
};

export const getBoxedDimensionsSvg = (productGroupId: number | undefined): string => {
  return `${serviceHelper.jbCdnBaseUrl}/products/packaged-dimensions/${productGroupId}.svg`;
};

export const getWithStandDimensionsSvg = (productGroupId: number | undefined): string => {
  return `${serviceHelper.jbCdnBaseUrl}/products/dimensions-with-specs/with-stand/${productGroupId}-with-stand.svg`;
};

export const getWithoutStandDimensionsSvg = (productGroupId: number | undefined): string => {
  return `${serviceHelper.jbCdnBaseUrl}/products/dimensions-with-specs/without-stand/${productGroupId}-without-stand.svg`;
};

export const getSpecificationDetails = (specificationName: string, metafields: ProductMetafields) => {
  const detail = metafields?.online_product?.value?.Display?.SpecificationDetails?.find(
    detail => specificationName === detail.Name.replace(/ \(.*?\)/, '')
  );
  if (!detail?.Values[0]) return null;
  return `${detail.Values[0]?.toString()}${
    (new RegExp(`${specificationName} \\((.*?)\\)`).exec(
      metafields?.online_product?.value?.Display?.SpecificationDetails?.find(detail =>
        detail.Name.includes(specificationName)
      )?.Name || ''
    ) || [])[1]
  }`;
};

const getSpecification = (specificationKeys: { [s: string]: string }, metafields: ProductMetafields) => {
  const specifications = Object.entries(specificationKeys)
    .map(([key, value]) => {
      return { [key]: getSpecificationDetails(value, metafields) };
    })
    ?.reduce((acc, curr) => ({ ...acc, ...curr }), {});

  return Object.values(specifications)?.every(x => x === null || x === '') ? null : specifications;
};

export const getDimensions = (metafields: ProductMetafields): { [x: string]: string | null } | null => {
  const dimensionKeys = {
    height: 'Product Height',
    width: 'Product Width',
    depth: 'Product Depth',
  };
  return getSpecification(dimensionKeys, metafields);
};

export const getBoxedDimensions = (metafields: ProductMetafields): { [x: string]: string | null } | null => {
  const dimensionKeys = {
    height: 'Packaged Height',
    width: 'Packaged Width',
    depth: 'Packaged Depth',
    weight: 'Packaged Weight',
  };
  return getSpecification(dimensionKeys, metafields);
};

export const getWithStandDimensions = (metafields: ProductMetafields): { [x: string]: string | null } | null => {
  const dimensionKeys = {
    height: 'Product Height with Stand',
    width: 'Product Width with Stand',
    depth: 'Product Depth with Stand',
    weight: 'Product Weight with Stand',
  };
  return getSpecification(dimensionKeys, metafields);
};

export const getWithoutStandDimensions = (metafields: ProductMetafields): { [x: string]: string | null } | null => {
  const dimensionKeys = {
    height: 'Product Height without Stand',
    width: 'Product Width without Stand',
    depth: 'Product Depth without Stand',
    weight: 'Product Weight without Stand',
  };
  return getSpecification(dimensionKeys, metafields);
};

export const useMicrosoft365Props = (algoliaProduct: AlgoliaProductHit, location = 'pdp') => {
  const { cart } = useCartContext();
  const microsoft365AttachMessaging = window.Jbhifi.microsoftAttachMessaging;
  const isInCart = !!cart?.items?.find(item => item.sku === algoliaProduct?.sku);

  return {
    heading: microsoft365AttachMessaging?.heading ?? '',
    headingLogoName: 'MicrosoftLogoIllustrated' as IconNameEnum,
    onAdd: async () => {
      setCartLoading(true);
      const properties = {
        _priceAtc: Math.round(algoliaProduct.price * 100),
        _maxQty: 1,
        _availabilityFulfilment: 'Digital',
        _availabilityCanPreOrder: algoliaProduct.availability?.canPreOrder,
        _eligibleForServices: Boolean(algoliaProduct.product?.eligibleForServices).toString(),
      };
      const updatedCart = await storefrontClient.addToCart(
        algoliaProduct?.variant_id,
        1,
        filterAndMapAttributes(properties)
      );
      trackAddToCart({
        location,
        currency: getStoreCurrency(),
        addedItems: transformAlgoliaHitForTracking(algoliaProduct, {
          listName: location === 'pdp' ? 'Add Microsoft 365 Checkbox' : 'Add Microsoft 365 Button',
          listSlot: location,
          listServiceConfig: undefined,
        }),
      });
      await updateCart(updatedCart);
    },
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    onRemove: () => {},
    attach: {
      features: microsoft365AttachMessaging?.mainBody ?? '',
      heading: microsoft365AttachMessaging?.subheading ?? '',
      subText: microsoft365AttachMessaging?.termsAndConditions ?? '',
    },
    hyperLinkText: microsoft365AttachMessaging?.mainBodyLinkText ?? '',
    hyperLinkUrl: microsoft365AttachMessaging?.mainBodyLinkUrl ?? '',
    alreadyInCart: isInCart,
    showMaxQtyAlert: showMaxQtyAlert,
    imageSrc: `${serviceHelper.jbCdnBaseUrl}/icons/pdp/ms-apps.png`,
  };
};

export const getShouldHideSyndicatedContent = (brand?: string) => {
  try {
    const hideSyndicatedContentBrands = window.Jbhifi?.pdpAccordion?.hideSyndicatedContentBrands || [];
    if (!hideSyndicatedContentBrands.length || !brand) return false;
    return hideSyndicatedContentBrands.includes(brand.toLowerCase());
  } catch (error) {
    console.error('getShouldHideSyndicatedContent', error);
    return false;
  }
};

export const getSyndicatedContentAccordionIndex = (sku: string, brand?: string) => {
  try {
    const syndicatedContentPlacementTop = {
      skus: window.Jbhifi?.pdpAccordion?.syndicatedContentTopSkus || [],
      brands: window.Jbhifi?.pdpAccordion?.syndicatedContentTopBrands || [],
    };

    if (
      brand &&
      !!syndicatedContentPlacementTop.brands.length &&
      syndicatedContentPlacementTop.brands.includes(brand.toLowerCase())
    ) {
      return 0;
    }

    if (!!syndicatedContentPlacementTop.skus.length && syndicatedContentPlacementTop.skus.includes(sku)) {
      return 0;
    }

    return 1;
  } catch (error) {
    console.error('getSyndicatedContentAccordionIndex', error);
    return 1;
  }
};

export const getInitialAccordionState = (sku: string, brand?: string) => {
  const isSyndicatedContentFirstTab = getSyndicatedContentAccordionIndex(sku, brand) === 0;
  const hasExpandInfoFromSupplier =
    isSyndicatedContentFirstTab && !window.Jbhifi?.pdpAccordion?.collapseInfoFromForTopSkus;
  const hasCollapseDescription =
    isSyndicatedContentFirstTab && window.Jbhifi?.pdpAccordion?.collapseDescriptionForTopSkus;

  const initialAccordionState = hasCollapseDescription ? [] : [AccordionIds.ProductOverview];

  if (window.featureFlags?.isPdpReviewsDefaultOpen) {
    initialAccordionState.push(AccordionIds.Reviews);
  }

  if (hasExpandInfoFromSupplier) {
    initialAccordionState.push(AccordionIds.FromTheSupplier);
  }

  return initialAccordionState;
};

export const formatStoresDataForAccordion = async (locationIds: Array<string>) => {
  if (locationIds.length === 0) return [];
  try {
    const algoliaLocationsData = await AlgoliaApiClient.getStoresByIds(locationIds);
    if (!Array.isArray(algoliaLocationsData) || algoliaLocationsData.length === 0) return [];

    const grouped = algoliaLocationsData.reduce((acc: Record<string, Array<string>>, loc: AlgoliaStoreData) => {
      const state = loc?.storeAddress?.State || 'Unknown';
      const storeName = loc?.storeName || '';
      if (!storeName) return acc;
      if (!acc[state]) acc[state] = [];
      if (!acc[state].includes(storeName)) acc[state].push(storeName);
      return acc;
    }, {});

    const sorted = Object.keys(grouped)
      .sort((a, b) => a.localeCompare(b))
      .map(state => ({ Name: state, Values: (grouped[state] || []).slice().sort((a, b) => a.localeCompare(b)) }));

    return sorted;
  } catch (error) {
    console.error('getLocalisationContent', error);
    return [];
  }
};
